import { Upload, Loader2, Sparkles, ArrowRight, Target, X, Plus } from 'lucide-react';
import { CLUBS } from '../../constants/clubs';
import { useAnalysisContext } from '../../contexts/AnalysisContext';
import { useCreditsContext } from '../../contexts/CreditsContext';
import { useUser } from '../../contexts/UserContext';

export function ImageUpload() {
  const { isAdmin } = useUser();
  const { credits, setShowPricingModal } = useCreditsContext();
  const {
    selectedImages,
    isAnalyzing,
    analysisProgress,
    selectedClub,
    setSelectedClub,
    playerContext,
    setPlayerContext,
    handleImageSelect,
    removeImage,
    analyzeSwing,
  } = useAnalysisContext();

  if (isAnalyzing) {
    return (
      <div className="py-20 text-center">
        <Loader2 className="h-20 w-20 animate-spin text-emerald-400 mx-auto mb-6" />
        <p className="text-2xl font-bold mb-3">Analyzing...</p>
        <p className="text-slate-400">{analysisProgress}</p>
      </div>
    );
  }

  if (selectedImages.length > 0) {
    return (
      <div className="space-y-6">
        {/* Image thumbnails */}
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {selectedImages.map((img, i) => (
            <div key={i} className="relative rounded-xl overflow-hidden bg-white/5 border border-white/10">
              <img
                src={img.preview}
                alt={`Swing frame ${i + 1}`}
                className="w-full aspect-[4/3] object-cover"
              />
              <button
                onClick={() => removeImage(i)}
                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center text-white hover:bg-red-500 transition"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-center py-1">
                <span className="text-[10px] text-slate-300 font-medium">Frame {i + 1}</span>
              </div>
            </div>
          ))}

          {/* Add more button */}
          {selectedImages.length < 7 && (
            <label className="cursor-pointer rounded-xl border-2 border-dashed border-white/20 bg-white/5 flex flex-col items-center justify-center aspect-[4/3] hover:border-emerald-500/40 hover:bg-white/10 transition">
              <Plus className="h-6 w-6 text-slate-400 mb-1" />
              <span className="text-xs text-slate-400">Add More</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageSelect}
              />
            </label>
          )}
        </div>

        <p className="text-xs text-slate-500 text-center">
          {selectedImages.length} of 7 photos selected
        </p>

        {/* Club Selection */}
        <div className="rounded-2xl bg-white/5 p-6 border border-white/10 text-center">
          <label className="block text-sm font-medium text-slate-300 mb-3">
            Which club are you swinging?
          </label>
          <select
            value={selectedClub}
            onChange={(e) => setSelectedClub(e.target.value)}
            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-base text-center"
            style={{ fontSize: '16px' }}
          >
            {CLUBS.map(club => (
              <option key={club.value} value={club.value}>
                {club.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-500 mt-2">
            This helps us analyze swing plane, tempo, and shot shape expectations
          </p>
        </div>

        {/* Player Context */}
        <div className="rounded-2xl bg-white/5 p-6 border border-white/10 text-center">
          <label className="block text-sm font-medium text-slate-300 mb-3">
            What are you working on? (optional)
          </label>
          <input
            type="text"
            value={playerContext}
            onChange={(e) => setPlayerContext(e.target.value)}
            placeholder="e.g., I've been slicing my driver..."
            maxLength={200}
            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-base text-center"
            style={{ fontSize: '16px' }}
            enterKeyHint="done"
          />
          <p className="text-xs text-slate-500 mt-2">
            Give Birdie context to make your analysis more targeted
          </p>
        </div>

        <button
          onClick={analyzeSwing}
          disabled={credits <= 0 && !isAdmin}
          className={`w-full flex items-center justify-center gap-3 rounded-2xl py-6 text-xl font-bold transition ${
            (credits > 0 || isAdmin)
              ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-black hover:scale-105'
              : 'bg-white/5 text-slate-500 cursor-not-allowed'
          }`}
        >
          <Sparkles className="h-6 w-6" />
          Analyze My Swing
          <ArrowRight className="h-6 w-6" />
        </button>
      </div>
    );
  }

  return (
    <label className={`block ${(credits > 0 || isAdmin) ? 'cursor-pointer' : 'cursor-not-allowed'} group relative`}>
      {/* Paywall Overlay */}
      {credits === 0 && !isAdmin && (
        <div className="absolute inset-0 z-10 rounded-3xl bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center p-6 sm:p-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 mb-4">
              <Target className="h-8 w-8 text-emerald-400" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Credits Required</h3>
            <p className="text-slate-400 mb-6">
              Purchase credits to get AI coaching on your swing
            </p>
            <button
              onClick={(e) => {
                e.preventDefault();
                setShowPricingModal(true);
              }}
              className="px-6 py-3 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 font-bold transition-all"
            >
              Get Credits
            </button>
          </div>
        </div>
      )}

      <div className="relative rounded-3xl border-2 border-dashed border-emerald-500/30 bg-gradient-to-b from-white/5 to-transparent p-6 sm:p-12 md:p-16 lg:p-20 hover:border-emerald-500/60 hover:bg-white/10 transition-all duration-300 active:scale-[0.98]">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-green-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="relative flex flex-col items-center gap-3 sm:gap-4">
          <div className="rounded-full bg-gradient-to-br from-emerald-500 to-green-500 p-3 sm:p-4 md:p-5 group-hover:scale-110 transition-transform">
            <Upload className="h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 text-black" />
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2">Upload swing photos</p>
            <p className="text-slate-400 text-xs sm:text-sm md:text-base">1-7 images from any angle</p>
          </div>
        </div>
      </div>
      <input
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleImageSelect}
      />
    </label>
  );
}
